import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  X, Phone, Mail, MessageCircle, Linkedin as LinkedinIcon,
  CheckCircle, Clock, SkipForward, XCircle,
  RefreshCw, ChevronDown, ChevronUp, Ban, Layers
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { formatInTz } from '../../utils/dateFormatters';
import { useTimezone } from '../../hooks/useTimezone';

interface StepLog {
  id: string;
  step_number: number;
  channel: string;
  task_type: string;
  status: string;
  outcome: string | null;
  skip_reason: string | null;
  deferred_to_step: number | null;
  deferred_check_count: number;
  scheduled_at: string | null;
  executed_at: string | null;
  completed_at: string | null;
  created_at: string;
}

interface OmniStep {
  step_order: number;
  task_type: string;
  json: {
    channel: string;
    day: number;
    description: string;
    call_slot: string | null;
    is_retry_check?: boolean;
  };
}

interface Props {
  leadId: string;
  leadName: string;
  companyName: string;
  onClose: () => void;
  campaignType?: 'omni' | 'inbound';
  campaignId?: string;
}

const CHANNEL_CONFIG: Record<string, { icon: React.ElementType; color: string; bg: string; label: string }> = {
  call: { icon: Phone, color: 'text-[#1875FF]', bg: 'bg-[#1875FF]/10 border-[#1875FF]/20', label: 'Call' },
  email: { icon: Mail, color: 'text-[#1875FF]', bg: 'bg-[#1875FF]/10 border-[#1875FF]/20', label: 'Email' },
  whatsapp: { icon: MessageCircle, color: 'text-[#05E0D1]', bg: 'bg-[#05E0D1]/10 border-[#05E0D1]/25', label: 'WhatsApp' },
  linkedin: { icon: LinkedinIcon, color: 'text-[#1875FF]', bg: 'bg-[#1875FF]/10 border-[#1875FF]/20', label: 'LinkedIn' },
};

const STATUS_CONFIG: Record<string, { icon: React.ElementType; color: string; bg: string; label: string }> = {
  completed: { icon: CheckCircle, color: 'text-[#05E0D1]', bg: 'bg-[#05E0D1]/10', label: 'Completed' },
  scheduled: { icon: Clock, color: 'text-[#1875FF]', bg: 'bg-[#1875FF]/10', label: 'Scheduled' },
  processing: { icon: RefreshCw, color: 'text-[#1875FF]', bg: 'bg-[#1875FF]/10', label: 'Processing' },
  skipped: { icon: SkipForward, color: 'text-gray-400', bg: 'bg-gray-50', label: 'Skipped' },
  deferred: { icon: RefreshCw, color: 'text-orange-500', bg: 'bg-orange-50', label: 'Deferred' },
  failed: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-50', label: 'Failed' },
  cancelled: { icon: Ban, color: 'text-gray-400', bg: 'bg-gray-50', label: 'Cancelled' },
};

const OmniStepTimeline: React.FC<Props> = ({ leadId, leadName, companyName, onClose, campaignType = 'omni', campaignId }) => {
  const tz = useTimezone();
  const [stepLogs, setStepLogs] = useState<StepLog[]>([]);
  const [omniSteps, setOmniSteps] = useState<OmniStep[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedStep, setExpandedStep] = useState<number | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      let stepsQuery = supabase
        .from('campaign_steps')
        .select('step_order, task_type, json')
        .eq('is_active', true)
        .order('step_order', { ascending: true });

      if (campaignId) {
        stepsQuery = stepsQuery.eq('campaign_id', campaignId);
      } else {
        stepsQuery = stepsQuery.eq('campaign_type', campaignType);
      }

      const [logsRes, stepsRes] = await Promise.all([
        supabase
          .from('campaign_step_logs')
          .select('*')
          .eq('lead_id', leadId)
          .eq('campaign_type', campaignType)
          .order('step_number', { ascending: true }),
        stepsQuery,
      ]);

      if (logsRes.data) setStepLogs(logsRes.data);
      if (stepsRes.data) setOmniSteps(stepsRes.data);
      setLoading(false);
    };

    fetchData();
  }, [leadId, campaignType, campaignId]);

  const completedCount = stepLogs.filter(s => s.status === 'completed').length;
  const scheduledCount = stepLogs.filter(s => s.status === 'scheduled' || s.status === 'processing').length;
  const skippedCount = stepLogs.filter(s => s.status === 'skipped').length;
  const cancelledCount = stepLogs.filter(s => s.status === 'cancelled').length;

  const activeChannels = new Set(stepLogs.filter(s => s.status === 'completed' || s.status === 'scheduled' || s.status === 'processing').map(s => s.channel));
  const completedChannels = new Set(stepLogs.filter(s => s.status === 'completed').map(s => s.channel));

  const currentDay = omniSteps.length > 0 ? (() => {
    const lastCompleted = [...stepLogs].filter(s => s.status === 'completed').sort((a, b) => b.step_number - a.step_number)[0];
    if (!lastCompleted) return 1;
    const stepDef = omniSteps.find(s => s.step_order === lastCompleted.step_number);
    return stepDef?.json?.day || 1;
  })() : 0;

  const formatTimestamp = (ts: string | null) => {
    if (!ts) return '--';
    try {
      return formatInTz(new Date(ts), 'MMM d, HH:mm', tz);
    } catch {
      return '--';
    }
  };

  const primarySteps = omniSteps;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col shadow-2xl shadow-black/20 animate-fadeIn">
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#05E0D1]/20 bg-gradient-to-r from-[#1875FF]/5 to-[#05E0D1]/5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center shadow-lg">
              <Layers className="text-white" size={18} />
            </div>
            <div>
              <h3 className="text-lg font-extrabold text-gray-900 font-display">
                {campaignType === 'inbound' ? 'Inbound Sequence' : 'Omni Sequence'}
              </h3>
              <p className="text-xs text-gray-500 font-medium">{leadName} - {companyName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-white rounded-lg"
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-6 py-4 border-b border-[#05E0D1]/15 grid grid-cols-3 sm:grid-cols-6 gap-3 bg-white">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">Day {currentDay}</div>
            <div className="text-xs text-gray-500 font-medium">Current</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-[#05E0D1]">{completedCount}</div>
            <div className="text-xs text-gray-500 font-medium">Completed</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-[#1875FF]">{scheduledCount}</div>
            <div className="text-xs text-gray-500 font-medium">Pending</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-400">{skippedCount}</div>
            <div className="text-xs text-gray-500 font-medium">Skipped</div>
          </div>
          {cancelledCount > 0 && (
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-400">{cancelledCount}</div>
              <div className="text-xs text-gray-500 font-medium">Cancelled</div>
            </div>
          )}
          <div className="text-center">
            <div className="flex items-center justify-center space-x-1.5">
              {['call', 'email', 'whatsapp', 'linkedin'].map(ch => {
                const config = CHANNEL_CONFIG[ch];
                const Icon = config.icon;
                const completed = completedChannels.has(ch);
                const active = activeChannels.has(ch);
                return (
                  <div
                    key={ch}
                    className={`w-7 h-7 rounded-full flex items-center justify-center transition-all ${
                      completed
                        ? 'bg-gradient-to-br from-[#1875FF]/15 to-[#05E0D1]/15 border border-[#05E0D1]/40 shadow-sm'
                        : active
                        ? 'bg-[#1875FF]/5 border border-[#1875FF]/20 animate-pulse'
                        : 'bg-gray-50 border border-gray-200'
                    }`}
                    title={`${config.label}${completed ? ' (active)' : active ? ' (in progress)' : ''}`}
                  >
                    <Icon size={13} className={completed ? config.color : active ? 'text-[#1875FF]/50' : 'text-gray-300'} />
                  </div>
                );
              })}
            </div>
            <div className="text-xs text-gray-500 font-medium mt-1">Channels</div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar bg-gray-50/50 px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-[#1875FF] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="relative">
              <div className="absolute left-[22px] top-0 bottom-0 w-px bg-gradient-to-b from-[#1875FF]/20 via-[#05E0D1]/15 to-gray-200" />
              {primarySteps.map((step) => {
                const log = stepLogs.find(l => l.step_number === step.step_order);
                const channelConf = CHANNEL_CONFIG[step.json.channel] || CHANNEL_CONFIG.call;
                const ChannelIcon = channelConf.icon;
                const status = log?.status || 'pending';
                const statusConf = STATUS_CONFIG[status] || { icon: Clock, color: 'text-gray-300', bg: 'bg-gray-50', label: 'Pending' };
                const StatusIcon = statusConf.icon;
                const isExpanded = expandedStep === step.step_order;
                const showPrevDay = step.step_order === 1 ||
                  (primarySteps.find(s => s.step_order === step.step_order - 1)?.json?.day !== step.json.day &&
                   primarySteps.findIndex(s => s.step_order === step.step_order) > 0);

                return (
                  <React.Fragment key={step.step_order}>
                    {showPrevDay && (
                      <div className="relative pl-12 pb-2 pt-1">
                        <div className="text-xs font-bold text-[#1875FF]/60 uppercase tracking-wider">
                          Day {step.json.day}
                        </div>
                      </div>
                    )}
                    <div
                      className={`relative pl-12 pb-3 cursor-pointer group`}
                      onClick={() => setExpandedStep(isExpanded ? null : step.step_order)}
                    >
                      <div className={`absolute left-[14px] top-1.5 w-[17px] h-[17px] rounded-full border-2 flex items-center justify-center z-10
                        ${status === 'completed' ? 'bg-[#05E0D1] border-[#05E0D1]' :
                          status === 'scheduled' || status === 'processing' ? 'bg-[#1875FF] border-[#1875FF]' :
                          status === 'failed' ? 'bg-red-500 border-red-500' :
                          status === 'deferred' ? 'bg-orange-400 border-orange-400' :
                          status === 'skipped' || status === 'cancelled' ? 'bg-gray-300 border-gray-300' :
                          'bg-white border-gray-300'
                        }`}
                      >
                        {status === 'completed' && <CheckCircle size={10} className="text-white" />}
                        {(status === 'scheduled' || status === 'processing') && <Clock size={9} className="text-white" />}
                        {status === 'failed' && <XCircle size={10} className="text-white" />}
                        {status === 'deferred' && <RefreshCw size={9} className="text-white" />}
                      </div>

                      <div className={`rounded-xl border p-3 transition-all group-hover:shadow-sm ${
                        status === 'completed' ? 'border-[#05E0D1]/30 bg-[#05E0D1]/5' :
                        status === 'scheduled' || status === 'processing' ? 'border-[#1875FF]/20 bg-[#1875FF]/5' :
                        status === 'failed' ? 'border-red-200 bg-red-50/30' :
                        status === 'deferred' ? 'border-orange-200 bg-orange-50/30' :
                        status === 'skipped' || status === 'cancelled' ? 'border-gray-200 bg-gray-50/50' :
                        'border-gray-200 bg-white'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2.5">
                            <div className={`w-7 h-7 rounded-lg flex items-center justify-center border ${channelConf.bg}`}>
                              <ChannelIcon size={14} className={channelConf.color} />
                            </div>
                            <div>
                              <div className="text-sm font-semibold text-gray-900">
                                {step.json.description}
                              </div>
                              <div className="flex items-center space-x-2 mt-0.5">
                                <span className={`text-xs font-medium ${statusConf.color}`}>
                                  {statusConf.label}
                                </span>
                                {step.json.call_slot && (
                                  <span className="text-xs text-gray-400">
                                    {step.json.call_slot}
                                  </span>
                                )}
                                {log?.completed_at && (
                                  <span className="text-xs text-gray-400">
                                    {formatTimestamp(log.completed_at)}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <StatusIcon size={16} className={statusConf.color} />
                            {isExpanded ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
                          </div>
                        </div>

                        {isExpanded && log && (
                          <div className="mt-3 pt-3 border-t border-[#05E0D1]/15 space-y-2 text-xs">
                            <div className="flex items-start space-x-2">
                              <span className="text-gray-500 font-medium w-20 shrink-0">Status:</span>
                              <span className={`font-medium ${statusConf.color}`}>{statusConf.label}</span>
                            </div>
                            {(status === 'skipped' || status === 'cancelled') && log.skip_reason && (
                              <div className="flex items-start space-x-2">
                                <span className="text-gray-500 font-medium w-20 shrink-0">Reason:</span>
                                <span className="text-gray-800">{log.skip_reason.replace(/_/g, ' ')}</span>
                              </div>
                            )}
                            {log.scheduled_at && (
                              <div className="flex items-start space-x-2">
                                <span className="text-gray-500 font-medium w-20 shrink-0">Scheduled:</span>
                                <span className="text-gray-800">{formatTimestamp(log.scheduled_at)}</span>
                              </div>
                            )}
                            {(log.executed_at || log.completed_at) && (
                              <div className="flex items-start space-x-2">
                                <span className="text-gray-500 font-medium w-20 shrink-0">Executed:</span>
                                <span className="text-gray-800">{formatTimestamp(log.executed_at || log.completed_at)}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-gray-100 bg-white">
          <p className="text-xs text-gray-400 text-center font-medium">
            {completedCount} of {primarySteps.length} steps completed
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default OmniStepTimeline;
